// noinspection ExceptionCaughtLocallyJS,JSUnusedGlobalSymbols

import { EndUserCertKeyType } from '@/sign/models/sign-constants';
import { SignPrivateKeyStrategy } from '@/sign/strategies/sign-private-key.strategy';
import { cloudKeySignService } from '@/services/cloud-key-sign.service';
import { determineHashAlgorithm, isSealCertificates } from '../helpers/sign.helper';
import { getLocale } from '@/helpers/locale-helper';
import { getRecaptchaTokenIfNotAuthenticated } from '@/helpers/recaptha-helper';
import { appSettingsHelper } from '@/helpers/settings-helper';
import { FileHashingResult, FileSigningResult } from '@/models/document-sign.models';
import { utilityService } from '@/services/utility.service';
import { base64ToArrayBuffer } from '@/helpers';

type Constructor<T> = new (...args: any[]) => T;

export function MixWithCloudKeyFileSystemKeyType<T extends Constructor<SignPrivateKeyStrategy>>(Base: T) {
  return class extends Base {
    protected async determineKeyTypes(): Promise<void> {
      if (appSettingsHelper.isCloudKeySupportingRsaAndEcdsa) {
        const { recaptchaToken, recaptchaType } = await getRecaptchaTokenIfNotAuthenticated();
        this.keyType = await cloudKeySignService.getKeyType(this.signingParams.data.certificate, recaptchaToken, recaptchaType);
      } else {
        this.keyType = EndUserCertKeyType.DSTU4145;
      }
    }
  };
}

export function MixWithCloudKeyFileSystemDocumentSigning<T extends Constructor<SignPrivateKeyStrategy>>(Base: T) {
  return class extends MixWithCloudKeyFileSystemKeyType(Base) {
    protected async isKeyPrivateKeyReaded(): Promise<boolean> {
      return true;
    }

    protected async signKeyTypeHashes(keyType: number, hashes: Array<FileHashingResult>, lib: any): Promise<Array<FileSigningResult>> {
      const hashAlgo = determineHashAlgorithm(this.keyType);
      for (const hash of hashes) {
        if (hash.isInternalNested) {
          let signContent = base64ToArrayBuffer(hash.sign);
          hash.hasheBase64 = await this.euSignLib.HashData(hashAlgo, signContent, true);
        }
      }

      const mappedHashes = hashes.map(h => h.hasheBase64);
      const { certificate, privateKeyPassword } = this.signingParams.data;

      let { recaptchaToken, recaptchaType } = await getRecaptchaTokenIfNotAuthenticated();

      const signs = await cloudKeySignService.signHashes(certificate, privateKeyPassword, mappedHashes,
        recaptchaToken, recaptchaType);

      if (this.signingParams.validateKeyType) {
        let certs = signs && signs.length ? await lib.GetSigner(signs[0], -1, false) : [];
        if (!this.isSeal && isSealCertificates(certs)) {
          throw new Error(getLocale('appSign.errors.selectedKeyIsNotSigningKey').toString());
        } else if (this.isSeal && !isSealCertificates(certs)) {
          throw new Error(getLocale('appSign.errors.selectedKeyIsNotSeal').toString());
        }
      }

      let signResults: FileSigningResult[] = [];
      for (let i = 0; i < signs.length; i++) {
        let hash = hashes[i];
        let signResult = new FileSigningResult({
          pDocumentVersionsFileCode: hash.pDocumentVersionsFileCode,
          pSignBase64: null,
          isInternalNested: hash.isInternalNested,
          documentVerFileSignDataId: hash.documentVerFileSignDataId
        });

        if (hash.isInternalNested) {
          let { recaptchaToken, recaptchaType } = await getRecaptchaTokenIfNotAuthenticated();

          let creationResult = await utilityService.createInternalNestedSign(hash.sign, signs[i], recaptchaToken, recaptchaType);
          if (!creationResult || !creationResult.newSignature) {
            throw new Error('Failed to create signature');
          }
          signResult.pSignBase64 = creationResult.newSignature;
        } else {
          signResult.pSignBase64 = signs[i];
        }

        signResults.push(signResult);
      }

      return signResults;
    }
  };
}
